// Testing Lab API client — all calls go through /api/testing-lab.
// Auth is automatic via the global fetch interceptor in App.js.

const API = '/api/testing-lab';

async function jfetch(path, options = {}) {
  const res = await fetch(`${API}${path}`, {
    headers: { 'Content-Type': 'application/json', ...(options.headers || {}) },
    credentials: 'include',
    ...options,
  });
  if (!res.ok) {
    let detail = `HTTP ${res.status}`;
    try {
      const j = await res.json();
      detail = j.detail || JSON.stringify(j);
    } catch { /* ignore */ }
    const err = new Error(typeof detail === 'string' ? detail : JSON.stringify(detail));
    err.status = res.status;
    err.detail = detail;
    throw err;
  }
  if (res.status === 204) return null;
  return res.json();
}

export const tl = {
  // Super admin
  superAdminMe: () => jfetch('/super-admin/me'),
  listSuperAdmins: () => jfetch('/super-admin/list'),
  grantSuperAdmin: (email) => jfetch('/super-admin/grant', { method: 'POST', body: JSON.stringify({ email }) }),
  revokeSuperAdmin: (email) => jfetch('/super-admin/revoke', { method: 'POST', body: JSON.stringify({ email }) }),

  // View As
  viewAsStart: (body) => jfetch('/view-as/start', { method: 'POST', body: JSON.stringify(body) }),
  viewAsStop: (body) => jfetch('/view-as/stop', { method: 'POST', body: JSON.stringify(body) }),
  viewAsLogs: (limit = 50) => jfetch(`/view-as/logs?limit=${limit}`),

  // Suites
  listSuites: () => jfetch('/suites'),
  createSuite: (body) => jfetch('/suites', { method: 'POST', body: JSON.stringify(body) }),
  updateSuite: (id, body) => jfetch(`/suites/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  deleteSuite: (id) => jfetch(`/suites/${id}`, { method: 'DELETE' }),

  // Cases
  listCases: (suiteId) => jfetch(`/cases${suiteId ? `?suite_id=${suiteId}` : ''}`),
  createCase: (body) => jfetch('/cases', { method: 'POST', body: JSON.stringify(body) }),
  updateCase: (id, body) => jfetch(`/cases/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  deleteCase: (id) => jfetch(`/cases/${id}`, { method: 'DELETE' }),

  // Sessions
  listSessions: () => jfetch('/sessions'),
  getSession: (id) => jfetch(`/sessions/${id}`),
  createSession: (body) => jfetch('/sessions', { method: 'POST', body: JSON.stringify(body) }),
  updateSession: (id, body) => jfetch(`/sessions/${id}`, { method: 'PUT', body: JSON.stringify(body) }),

  // Evidence
  listEvidence: (sessionId) => jfetch(`/sessions/${sessionId}/evidence`),
  uploadEvidence: async (sessionId, file, fields = {}) => {
    const fd = new FormData();
    fd.append('file', file);
    Object.entries(fields).forEach(([k, v]) => v != null && fd.append(k, v));
    const res = await fetch(`${API}/sessions/${sessionId}/evidence/upload`, {
      method: 'POST', body: fd, credentials: 'include',
    });
    if (!res.ok) throw new Error(`Upload failed: ${res.status}`);
    return res.json();
  },
  addNoteEvidence: (sessionId, body) => jfetch(`/sessions/${sessionId}/evidence/note`, {
    method: 'POST', body: JSON.stringify(body),
  }),

  // Findings
  listFindings: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return jfetch(`/findings${qs ? `?${qs}` : ''}`);
  },
  getFinding: (id) => jfetch(`/findings/${id}`),
  createFinding: (body) => jfetch('/findings', { method: 'POST', body: JSON.stringify(body) }),
  updateFinding: (id, body) => jfetch(`/findings/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  fixPrompts: (id) => jfetch(`/findings/${id}/fix-prompts`),
  riskAudits: (id) => jfetch(`/findings/${id}/risk-audits`),

  // Akro
  akroChat: (body) => jfetch('/akro/chat', { method: 'POST', body: JSON.stringify(body) }),
  akroMessages: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return jfetch(`/akro/messages${qs ? `?${qs}` : ''}`);
  },
  akroMSC: (body) => jfetch('/akro/must-should-could', { method: 'POST', body: JSON.stringify(body) }),
  akroFixPrompt: (findingId) => jfetch('/akro/fix-prompt', { method: 'POST', body: JSON.stringify({ finding_id: findingId }) }),
  akroRiskAudit: (findingId, fixSummary) => jfetch('/akro/risk-audit', { method: 'POST', body: JSON.stringify({ finding_id: findingId, fix_summary: fixSummary }) }),
  akroDocProposal: (body) => jfetch('/akro/doc-proposal', { method: 'POST', body: JSON.stringify(body) }),

  // Features
  listFeatures: (module) => jfetch(`/features${module ? `?module=${module}` : ''}`),
  createFeature: (body) => jfetch('/features', { method: 'POST', body: JSON.stringify(body) }),
  updateFeature: (id, body) => jfetch(`/features/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  deleteFeature: (id) => jfetch(`/features/${id}`, { method: 'DELETE' }),

  // Code refs
  listCodeRefs: (featureId) => jfetch(`/code-refs${featureId ? `?feature_id=${featureId}` : ''}`),
  createCodeRef: (body) => jfetch('/code-refs', { method: 'POST', body: JSON.stringify(body) }),

  // Docs
  listDocFiles: () => jfetch('/docs/files'),
  createDocFile: (body) => jfetch('/docs/files', { method: 'POST', body: JSON.stringify(body) }),
  getDocFile: (id) => jfetch(`/docs/files/${id}`),
  listDocSections: (id) => jfetch(`/docs/files/${id}/sections`),
  listDocVersions: (id) => jfetch(`/docs/files/${id}/versions`),
  updateDocContent: (id, body) => jfetch(`/docs/files/${id}/content`, { method: 'PUT', body: JSON.stringify(body) }),
  archiveDocFile: (id) => jfetch(`/docs/files/${id}`, { method: 'DELETE' }),

  // Doc proposals
  listProposals: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return jfetch(`/docs/proposals${qs ? `?${qs}` : ''}`);
  },
  createProposal: (body) => jfetch('/docs/proposals', { method: 'POST', body: JSON.stringify(body) }),
  decideProposal: (id, decision, notes) => jfetch(`/docs/proposals/${id}/decision`, {
    method: 'POST', body: JSON.stringify({ decision, reviewer_notes: notes }),
  }),
  applyProposal: (id) => jfetch(`/docs/proposals/${id}/apply`, { method: 'POST' }),

  // Auto-plan (Test This Now)
  suitesByRoute: (route) => jfetch(`/suites/by-route?route=${encodeURIComponent(route)}`),
  akroAutoPlan: (body) => jfetch('/akro/auto-plan', { method: 'POST', body: JSON.stringify(body) }),

  // Step runner (live test execution)
  startCaseInSession: (sessionId, caseId) => jfetch(`/sessions/${sessionId}/start-case/${caseId}`, { method: 'POST' }),
  listStepRuns: (sessionId, caseId = null) => {
    const q = caseId ? `?case_id=${encodeURIComponent(caseId)}` : '';
    return jfetch(`/sessions/${sessionId}/step-runs${q}`);
  },
  updateStepRun: (runId, body) => jfetch(`/step-runs/${runId}`, { method: 'PATCH', body: JSON.stringify(body) }),

  // Sign off
  signOffSession: (sessionId, body) => jfetch(`/sessions/${sessionId}/sign-off`, { method: 'POST', body: JSON.stringify(body) }),

  // ----- Quick Review v2 (page-context plain-English card stack) -----
  quickPlan: (body) => jfetch('/quick-review/plan', { method: 'POST', body: JSON.stringify(body) }),
  quickSubmit: (body) => jfetch('/quick-review/submit', { method: 'POST', body: JSON.stringify(body) }),
  findingsGrouped: (status) => jfetch(`/findings-by-route${status ? `?status=${encodeURIComponent(status)}` : ''}`),
  findingMiniPRD: (id) => jfetch(`/findings/${id}/mini-prd`),
  generateChangeOrder: (body) => jfetch('/change-orders/generate', { method: 'POST', body: JSON.stringify(body) }),
  listChangeOrders: () => jfetch('/change-orders'),
  getChangeOrder: (id) => jfetch(`/change-orders/${id}`),

  // ----- Testing Wizard (curated module blocks + Bug & Enhancement Reporting) -----
  wizardModules: () => jfetch('/wizard/modules'),
  wizardModuleDetail: (moduleId) => jfetch(`/wizard/modules/${moduleId}`),
  wizardChat: (body) => jfetch('/wizard/chat', { method: 'POST', body: JSON.stringify(body) }),
  wizardCreateReport: (body) => jfetch('/wizard/reports', { method: 'POST', body: JSON.stringify(body) }),
  wizardListReports: (params = {}) => {
    const q = new URLSearchParams(Object.entries(params).filter(([, v]) => v != null && v !== '')).toString();
    return jfetch(`/wizard/reports${q ? `?${q}` : ''}`);
  },
  wizardGetReport: (id) => jfetch(`/wizard/reports/${id}`),
  wizardUpdateReportStatus: (id, status) =>
    jfetch(`/wizard/reports/${id}/status`, { method: 'PATCH', body: JSON.stringify({ wizard_status: status }) }),
  wizardDeleteReport: (id) => jfetch(`/wizard/reports/${id}`, { method: 'DELETE' }),
  wizardExportReports: async (reportIds, format) => {
    const base = process.env.REACT_APP_BACKEND_URL || '';
    const res = await fetch(`${base}/api/testing-lab/wizard/reports/export`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ report_ids: reportIds, format }),
    });
    if (!res.ok) {
      const text = await res.text().catch(() => '');
      throw new Error(text || `Export failed (${res.status})`);
    }
    const blob = await res.blob();
    const disp = res.headers.get('content-disposition') || '';
    const match = disp.match(/filename="([^"]+)"/);
    const filename = match ? match[1] : `wizard-reports.${format}`;
    return { blob, filename, count: parseInt(res.headers.get('x-wizard-report-count') || '0', 10) };
  },
};

export default tl;
